<template>
  <div class="dashboard-container">
    <!-- Page Header -->
    <div class="page-header">
      <h1>Dashboard</h1>
      <div class="header-actions">
        <button class="btn btn-primary" @click="refreshData">
          <i class="icon-refresh">🔄</i> Refresh
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
      <p>Loading dashboard data...</p>
    </div>

    <!-- Dashboard Content -->
    <div v-else class="dashboard-content">
      <div class="content-main">
      <!-- Main Metrics Cards -->
      <div class="metrics-grid">
        <!-- Workstreams Card -->
        <div class="metric-card workstreams-card">
          <div class="card-header">
            <div class="card-icon">🎯</div>
            <div class="card-info">
              <h3>Workstreams</h3>
              <p class="metric-value">{{ dashboardData.workstreams.total }}</p>
            </div>
          </div>
           <div class="card-stats">
             <div class="stat-item">
               <span class="stat-label">Workstreams:</span>
               <span class="stat-value">{{ dashboardData.workstreams.total }}</span>
             </div>
             <div class="stat-item">
               <span class="stat-label">Members:</span>
               <span class="stat-value">{{ dashboardData.workstreams.totalMembers }}</span>
             </div>
           </div>
        </div>

         <!-- Goals Card -->
         <div class="metric-card goals-card">
           <div class="card-header">
             <div class="card-icon">🎯</div>
             <div class="card-info">
               <h3>Goals</h3>
               <p class="metric-value">{{ dashboardData.goals.averageProgress }}%</p>
             </div>
           </div>
           <div class="card-stats">
             <div class="stat-item">
               <span class="stat-label">Active:</span>
               <span class="stat-value">{{ dashboardData.goals.active }}</span>
             </div>
             <div class="stat-item">
               <span class="stat-label">Total:</span>
               <span class="stat-value">{{ dashboardData.goals.total }}</span>
             </div>
           </div>
         </div>

         <!-- Tasks Card -->
         <div class="metric-card tasks-card">
           <div class="card-header">
             <div class="card-icon">✓</div>
             <div class="card-info">
               <h3>Tasks</h3>
             </div>
           </div>
           <div class="card-stats">
             <div class="stat-item">
               <span class="stat-label">To-Do:</span>
               <span class="stat-value">{{ dashboardData.tasks.to_do }}</span>
             </div>
             <div class="stat-item">
               <span class="stat-label">In Progress:</span>
               <span class="stat-value">{{ dashboardData.tasks.in_progress }}</span>
             </div>
             <div class="stat-item">
               <span class="stat-label">Done:</span>
               <span class="stat-value">{{ dashboardData.tasks.done }}</span>
             </div>
           </div>
         </div>

        <!-- Agents Card (commented out on 2025-12-01)
        <div class="metric-card agents-card">
          <div class="card-header">
            <div class="card-icon">🤖</div>
            <div class="card-info">
              <h3>Agents</h3>
              <p class="metric-value">{{ dashboardData.agents.total }}</p>
            </div>
          </div>
          <div class="card-stats">
            <div class="stat-item">
              <span class="stat-label">Active:</span>
              <span class="stat-value">{{ dashboardData.agents.active }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">Inactive:</span>
              <span class="stat-value">{{ dashboardData.agents.inactive }}</span>
            </div>
          </div>
          <div class="card-trend">
            <span class="trend-indicator" :class="{ 'positive': dashboardData.agents.trend > 0, 'negative': dashboardData.agents.trend < 0 }">
              {{ dashboardData.agents.trend > 0 ? '↗' : dashboardData.agents.trend < 0 ? '↘' : '→' }}
              {{ Math.abs(dashboardData.agents.trend) }}% vs last month
            </span>
          </div>
        </div>
        -->

        <!-- Conversations Card (commented out on 2025-12-01)
        <div class="metric-card conversations-card">
          <div class="card-header">
            <div class="card-icon">💬</div>
            <div class="card-info">
              <h3>Conversations</h3>
              <p class="metric-value">{{ dashboardData.conversations.total }}</p>
            </div>
          </div>
          <div class="card-stats">
            <div class="stat-item">
              <span class="stat-label">Today:</span>
              <span class="stat-value">{{ dashboardData.conversations.today }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">This Week:</span>
              <span class="stat-value">{{ dashboardData.conversations.this_week }}</span>
            </div>
          </div>
          <div class="card-trend">
            <span class="trend-indicator" :class="{ 'positive': dashboardData.conversations.trend > 0, 'negative': dashboardData.conversations.trend < 0 }">
              {{ dashboardData.conversations.trend > 0 ? '↗' : dashboardData.conversations.trend < 0 ? '↘' : '→' }}
              {{ Math.abs(dashboardData.conversations.trend) }}% vs last week
            </span>
          </div>
        </div>
        -->

        <!-- Models Card (commented out on 2025-12-01)
        <div class="metric-card models-card">
          <div class="card-header">
            <div class="card-icon">🧠</div>
            <div class="card-info">
              <h3>Models</h3>
              <p class="metric-value">{{ dashboardData.models.total }}</p>
            </div>
          </div>
          <div class="card-stats">
            <div class="stat-item">
              <span class="stat-label">Active:</span>
              <span class="stat-value">{{ dashboardData.models.active }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">Archived:</span>
              <span class="stat-value">{{ dashboardData.models.archived }}</span>
            </div>
          </div>
        </div>
        -->

        <!-- Users Card (commented out on 2025-12-01)
        <div class="metric-card users-card">
          <div class="card-header">
            <div class="card-icon">👥</div>
            <div class="card-info">
              <h3>Users</h3>
              <p class="metric-value">{{ dashboardData.users.total }}</p>
            </div>
          </div>
          <div class="card-stats">
            <div class="stat-item">
              <span class="stat-label">Active:</span>
              <span class="stat-value">{{ dashboardData.users.active }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">New:</span>
              <span class="stat-value">{{ dashboardData.users.new }}</span>
            </div>
          </div>
          <div class="card-trend">
            <span class="trend-indicator" :class="{ 'positive': dashboardData.users.trend > 0, 'negative': dashboardData.users.trend < 0 }">
              {{ dashboardData.users.trend > 0 ? '↗' : dashboardData.users.trend < 0 ? '↘' : '→' }}
              {{ Math.abs(dashboardData.users.trend) }}% vs last month
            </span>
          </div>
        </div>
        -->

        <!-- Token Usage Card (commented out on 2025-12-01)
        <div class="metric-card tokens-card">
          <div class="card-header">
            <div class="card-icon">🪙</div>
            <div class="card-info">
              <h3>Token Usage</h3>
              <p class="metric-value">{{ formatNumber(dashboardData.tokens.used) }}</p>
            </div>
          </div>
          <div class="card-stats">
            <div class="stat-item">
              <span class="stat-label">Available:</span>
              <span class="stat-value">{{ formatNumber(dashboardData.tokens.available) }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">Limit:</span>
              <span class="stat-value">{{ formatNumber(dashboardData.tokens.limit) }}</span>
            </div>
          </div>
          <div class="progress-bar">
            <div class="progress-fill" :style="{ width: tokenUsagePercent + '%' }"></div>
          </div>
        </div>
        -->
      </div>

      <!-- My Asks Table (same as /workstreams?view=my-asks) -->
      <div class="table-section">
        <MyTasksTable :max-rows="10" />
      </div>
      
      <!-- Secondary Section -->
      <div class="secondary-section">
        <!-- Recent Activity (commented out on 2025-12-01)
        <div class="activity-section">
          <h2>Recent Activity</h2>
          <div class="activity-list">
            <div v-for="activity in dashboardData.recentActivity" :key="activity.id" class="activity-item">
              <div class="activity-icon">{{ activity.icon }}</div>
              <div class="activity-content">
                <p class="activity-title">{{ activity.title }}</p>
                <p class="activity-description">{{ activity.description }}</p>
                <span class="activity-time">{{ formatTime(activity.timestamp) }}</span>
              </div>
            </div>
            <div v-if="dashboardData.recentActivity.length === 0" class="empty-state">
              <p>No recent activity</p>
            </div>
          </div>
        </div>
        -->

        <!-- Quick Actions (commented out on 2025-12-01)
        <div class="quick-actions-section">
          <h2>Quick Actions</h2>
          <div class="quick-actions-grid">
            <button class="quick-action-btn" @click="$router.push('/agents')">
              <div class="action-icon">🤖</div>
              <span>Manage Agents</span>
            </button>
            <button class="quick-action-btn" @click="$router.push('/workstreams')">
              <div class="action-icon">🎯</div>
              <span>Workstreams</span>
            </button>
            <button class="quick-action-btn" @click="$router.push('/organization')">
              <div class="action-icon">👥</div>
              <span>Organization</span>
            </button>
            <button class="quick-action-btn" @click="$router.push('/settings')">
              <div class="action-icon">⚙️</div>
              <span>Settings</span>
            </button>
          </div>
        </div>
        -->
      </div>

      <!-- Agent Status (commented out on 2025-12-01)
      <div class="agent-status">
        <h2>Agent Status</h2>
        <div v-if="dashboardData.agents.running && dashboardData.agents.running.length > 0" class="agent-list">
          <div v-for="agent in dashboardData.agents.running" :key="agent.id" class="agent-item">
            <div class="agent-indicator running"></div>
            <div class="agent-info">
              <span class="agent-name">{{ agent.name }}</span>
              <span class="agent-meta">{{ agent.conversations || 0 }} active conversations</span>
            </div>
          </div>
        </div>
        <div v-else class="empty-state">
          <p>No agents currently running</p>
        </div>
        <div class="status-summary">
          <div class="status-item">
            <div class="status-indicator healthy"></div>
            <span>API Server</span>
          </div>
          <div class="status-item">
            <div class="status-indicator" :class="dashboardData.systemStatus.database"></div>
            <span>Database</span>
          </div>
          <div class="status-item">
            <div class="status-indicator" :class="dashboardData.systemStatus.storage"></div>
            <span>Storage</span>
          </div>
        </div>
      </div>
      -->
      </div>
      <!-- Right Panel (commented out on 2025-12-06)
      <aside class="right-panel" aria-label="User Activity">
        <h2>User Activity</h2>
        <div class="panel-body">
          <div v-if="dashboardData.recentActivity.length === 0" class="empty-state">
            <p>No recent activity</p>
          </div>
          <div v-else class="activity-list">
            <div v-for="activity in dashboardData.recentActivity" :key="activity.id" class="activity-item">
              <div class="activity-icon">{{ activity.icon || '📝' }}</div>
              <div class="activity-content">
                <p class="activity-title">{{ activity.title }}</p>
                <p class="activity-description">{{ activity.description }}</p>
                <span class="activity-time">{{ formatTime(activity.timestamp) }}</span>
              </div>
            </div>
          </div>
        </div>
      </aside>
      -->
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { api } from '@/services/api'
import MyTasksTable from '@/components/workstream/MyTasksTable.vue'

export default {
  name: 'DashboardView',
  components: { MyTasksTable },
  setup() {
    const store = useStore()
    const loading = ref(true)
    const dashboardData = ref({
      workstreams: {
        total: 0,
        active: 0,
        draft: 0,
        totalMembers: 0
      },
      goals: {
        total: 0,
        active: 0,
        completed: 0,
        averageProgress: 0
      },
      tasks: {
        to_do: 0,
        in_progress: 0,
        done: 0
      },
      agents: {
        total: 0,
        active: 0,
        inactive: 0,
        trend: 0,
        running: []
      },
      conversations: {
        total: 0,
        today: 0,
        this_week: 0,
        trend: 0
      },
      models: {
        total: 0,
        active: 0,
        archived: 0
      },
      users: {
        total: 0,
        active: 0,
        new: 0,
        trend: 0
      },
      tokens: {
        used: 0,
        available: 0,
        limit: 0
      },
      recentActivity: [],
      systemStatus: {
        api: 'healthy',
        database: 'healthy',
        agents: 'healthy',
        storage: 'healthy'
      }
    })

    const tokenUsagePercent = computed(() => {
      if (dashboardData.value.tokens.limit === 0) return 0
      return Math.min(100, (dashboardData.value.tokens.used / dashboardData.value.tokens.limit) * 100)
    })

    const formatNumber = (num) => {
      if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
      if (num >= 1000) return (num / 1000).toFixed(1) + 'K'
      return num.toString()
    }

    const formatTime = (timestamp) => {
      const date = new Date(timestamp)
      const now = new Date()
      const diff = now - date
      const minutes = Math.floor(diff / 60000)
      const hours = Math.floor(minutes / 60)
      const days = Math.floor(hours / 24)

      if (days > 0) return `${days}d ago`
      if (hours > 0) return `${hours}h ago`
      if (minutes > 0) return `${minutes}m ago`
      return 'Just now'
    }


    const loadDashboardData = async () => {
      try {
        loading.value = true
        
        // Fetch all metrics in parallel
        const [
          agentsMetrics,
          conversationsMetrics,
          usersMetrics,
          tokensMetrics,
          recentActivity,
          systemStatus,
          workstreamsStats,
          agentsData,
          modelsData
        ] = await Promise.all([
          api.get('/api/dashboard/agents').then(r => r.data).catch(() => ({ total: 0, active: 0, inactive: 0, trend: 0 })),
          api.get('/api/dashboard/conversations').then(r => r.data).catch(() => ({ total: 0, today: 0, this_week: 0, trend: 0 })),
          api.get('/api/dashboard/users').then(r => r.data).catch(() => ({ total: 0, active: 0, new: 0, trend: 0 })),
          api.get('/api/dashboard/tokens').then(r => r.data).catch(() => ({ used: 0, available: 0, limit: 0 })),
          api.get('/api/dashboard/activity').then(r => r.data).catch(() => []),
          api.get('/api/dashboard/status').then(r => r.data).catch(() => ({ api: 'healthy', database: 'healthy', agents: 'healthy', storage: 'healthy' })),
          api.get('/api/workstreams/stats').then(r => r.data).catch(() => null),
          api.get('/api/agents').then(r => r.data).catch(() => []),
          api.get('/api/llms/models').then(r => r.data).catch(() => [])
        ])

        // Process workstreams data
        if (workstreamsStats) {
          dashboardData.value.workstreams = {
            total: workstreamsStats.total_workstreams || 0,
            active: workstreamsStats.active_workstreams || 0,
            draft: (workstreamsStats.total_workstreams || 0) - (workstreamsStats.active_workstreams || 0)
          }
        }

        // Calculate goals and workstreams members from workstreams
        try {
          const workstreamsResponse = await api.get('/api/workstreams')
          const workstreams = Array.isArray(workstreamsResponse.data) ? workstreamsResponse.data : (workstreamsResponse.data?.workstreams || [])

          let totalGoals = 0
          let activeGoals = 0
          let completedGoals = 0
          let totalMembers = 0
          let totalProgress = 0
          let progressCount = 0

          workstreams.forEach(ws => {
            if (ws.members && Array.isArray(ws.members)) {
              totalMembers += ws.members.length
            }
            if (ws.goals && Array.isArray(ws.goals)) {
              totalGoals += ws.goals.length
              ws.goals.forEach(goal => {
                if (goal.status === 'completed') {
                  completedGoals++
                } else if (goal.status === 'active' || goal.status === 'in_progress') {
                  activeGoals++
                }
                if (goal.progress !== undefined && goal.progress !== null) {
                  totalProgress += goal.progress
                  progressCount++
                }
              })
            }
          })

          dashboardData.value.workstreams.totalMembers = totalMembers
          dashboardData.value.goals = {
            total: totalGoals,
            active: activeGoals,
            completed: completedGoals,
            averageProgress: progressCount > 0 ? Math.round(totalProgress / progressCount) : 0
          }
        } catch (e) {
          console.error('Failed to calculate goals', e)
        }

        // Process agents data
        const agentsList = Array.isArray(agentsData) ? agentsData : (agentsData?.agents || [])
        dashboardData.value.agents = {
          ...agentsMetrics,
          running: agentsList.filter(a => a.status === 'active').map(a => ({
            id: a.id || a._id,
            name: a.name,
            conversations: a.conversation_count || 0
          }))
        }

        // Process models data
        const modelsList = Array.isArray(modelsData) ? modelsData : (modelsData?.models || [])
        dashboardData.value.models = {
          total: modelsList.length,
          active: modelsList.filter(m => !m.archived).length,
          archived: modelsList.filter(m => m.archived).length
        }

        // Assign other metrics
        dashboardData.value.conversations = conversationsMetrics
        dashboardData.value.users = usersMetrics
        dashboardData.value.tokens = tokensMetrics
        dashboardData.value.recentActivity = Array.isArray(recentActivity) ? recentActivity : []
        dashboardData.value.systemStatus = systemStatus

        // Get tasks data directly from API
        try {
          const tasksResponse = await api.get('/api/tasks')
          // Backend returns {tasks: [...]}
          const tasksList = Array.isArray(tasksResponse.data?.tasks) ? tasksResponse.data.tasks :
                            Array.isArray(tasksResponse.data) ? tasksResponse.data : []

          dashboardData.value.tasks = {
            to_do: tasksList.filter(t => t.status === 'to_do').length,
            in_progress: tasksList.filter(t => t.status === 'in_progress').length,
            done: tasksList.filter(t => t.status === 'done').length
          }
        } catch (e) {
          console.error('Failed to load tasks', e)
          dashboardData.value.tasks = {
            to_do: 0,
            in_progress: 0,
            done: 0
          }
        }

      } catch (error) {
        console.error('Error loading dashboard data:', error)
      } finally {
        loading.value = false
      }
    }

    const refreshData = async () => {
      await loadDashboardData()
    }

    onMounted(() => {
      loadDashboardData()
    })

    return {
      loading,
      dashboardData,
      tokenUsagePercent,
      formatNumber,
      formatTime,
      refreshData
    }
  }
}
</script>

<style scoped>
.dashboard-container {
  min-height: 100vh;
  background: #f8f9fa;
  padding: 2rem;
}

/* Header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: #ffffff;
  color: #2c3e50;
  border-bottom: 1px solid #e9ecef;
  margin-bottom: 1rem;
}

.page-header h1 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.5rem;
  font-weight: 600;
}

.header-actions {
  display: flex;
  gap: 1rem;
}

.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-primary:hover {
  background: #5a6fd8;
  transform: translateY(-1px);
}

/* Loading */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem;
  color: #6c757d;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Metrics Grid */
.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.metric-card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  transition: transform 0.2s ease;
}

.metric-card:hover {
  transform: translateY(-2px);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.card-icon {
  font-size: 2rem;
  width: 60px;
  height: 60px;
  background: #f8f9fa;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.card-info h3 {
  margin: 0 0 0.25rem 0;
  color: #6c757d;
  font-size: 0.9rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.metric-value {
  margin: 0;
  color: #2c3e50;
  font-size: 2rem;
  font-weight: 700;
}

.card-stats {
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
  padding: 1rem 0;
  border-top: 1px solid #f1f3f4;
}

.tasks-card .card-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.stat-label {
  font-size: 0.75rem;
  color: #6c757d;
  margin-bottom: 0.25rem;
}

.stat-value {
  font-size: 1.1rem;
  font-weight: 600;
  color: #2c3e50;
}

.card-trend {
  display: flex;
  justify-content: center;
}

.trend-indicator {
  font-size: 0.8rem;
  font-weight: 500;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
}

.trend-indicator.positive {
  background: #d4edda;
  color: #155724;
}

.trend-indicator.negative {
  background: #f8d7da;
  color: #721c24;
}

/* Progress Bar */
.progress-bar {
  height: 8px;
  background: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
  margin-top: 1rem;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  transition: width 0.3s ease;
}

/* Table Section */
.table-section {
  margin-bottom: 2rem;
}

/* Right Panel Layout (commented out on 2025-12-06)
.dashboard-content {
  display: grid;
  grid-template-columns: 1fr 340px;
  gap: 1.5rem;
  align-items: start;
}

.content-main {
  min-width: 0;
}

.right-panel {
  position: sticky;
  top: 1rem;
  height: 100vh;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  padding: 1rem;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.right-panel h2 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.25rem;
  font-weight: 600;
}

.right-panel .panel-body {
  margin-top: 1rem;
  overflow-y: auto;
  flex: 1;
}

.right-panel .activity-list {
  max-height: none;
}
*/

/* Secondary Section */
.secondary-section {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 2rem;
  margin-bottom: 2rem;
}

.activity-section,
.quick-actions-section {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.activity-section h2,
.quick-actions-section h2 {
  margin: 0 0 1.5rem 0;
  color: #2c3e50;
  font-size: 1.25rem;
  font-weight: 600;
}

.activity-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-height: 400px;
  overflow-y: auto;
}

.activity-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem;
  border-radius: 8px;
  background: #f8f9fa;
}

.activity-icon {
  font-size: 1.5rem;
  width: 40px;
  height: 40px;
  background: white;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.activity-content {
  flex: 1;
}

.activity-title {
  margin: 0 0 0.25rem 0;
  font-weight: 600;
  color: #2c3e50;
}

.activity-description {
  margin: 0 0 0.5rem 0;
  color: #6c757d;
  font-size: 0.9rem;
}

.activity-time {
  font-size: 0.8rem;
  color: #adb5bd;
}

.quick-actions-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.quick-action-btn {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.quick-action-btn:hover {
  background: #e9ecef;
  border-color: #667eea;
}

.action-icon {
  font-size: 1.5rem;
}

/* Agent Status */
.agent-status {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.agent-status h2 {
  margin: 0 0 1.5rem 0;
  color: #2c3e50;
  font-size: 1.25rem;
  font-weight: 600;
}

.agent-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}

.agent-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 8px;
}

.agent-indicator {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.agent-indicator.running {
  background: #28a745;
  box-shadow: 0 0 8px rgba(40, 167, 69, 0.4);
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.agent-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.agent-name {
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.9rem;
}

.agent-meta {
  font-size: 0.75rem;
  color: #6c757d;
}

.status-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #f1f3f4;
}

.status-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.status-indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.status-indicator.healthy {
  background: #28a745;
}

.status-indicator.warning {
  background: #ffc107;
}

.status-indicator.error {
  background: #dc3545;
}

.empty-state {
  text-align: center;
  color: #9ca3af;
  padding: 2rem;
  font-size: 14px;
}

.empty-state p {
  margin: 0;
}

/* Responsive Design */
@media (max-width: 768px) {
  .dashboard-container {
    padding: 1rem;
  }
  
  .dashboard-header {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
  
  .metrics-grid {
    grid-template-columns: 1fr;
  }
  
  .secondary-section {
    grid-template-columns: 1fr;
  }
  
  .card-stats {
    flex-direction: column;
    gap: 1rem;
  }
  
  .stat-item {
    flex-direction: row;
    justify-content: space-between;
  }
  
  .quick-actions-grid {
    grid-template-columns: 1fr;
  }
}

/* Mobile adjustments for right panel (commented out on 2025-12-06)
@media (max-width: 768px) {
  .dashboard-content { grid-template-columns: 1fr; }
  .right-panel { position: static; height: auto; }
}
*/
</style>
